// Wallet portfolio read API (/v1/wallets/:address/*). Public and
// address-scoped — the same call serves your own wallet and someone else's,
// which is what makes "view another wallet" work without an account.
//
// Read-only, and served only when the operator turns on WT_USERLAND_READ_ENABLED.
// With the gate off the routes are not registered, so every call 404s; callers
// treat that as an honest offline state rather than an error to surface.

import { countsGasInPnL } from '../components/settings/SettingsModal'
import { ApiError, apiFetch } from './client'
import type { LiveBalancePreview } from './userland'

// Gas is a real cost of trading but not one we take, so PnL leaves it out
// unless the account asked for it (Settings -> Trading). The server decides;
// the response says which number it returned (pnl_includes_network_fees), so
// the two can never silently disagree.
function pnlCostQuery(): string {
  try {
    return countsGasInPnL() ? '?include_network_fees=1' : ''
  } catch {
    return ''
  }
}

// WireHolding mirrors the backend `holding` struct verbatim. The identity
// fields (ticker/name/image/launchpad) are joined from the indexed token
// tables and are absent for a position in a token we have not indexed.
export type WireHolding = {
  token_master: string
  token_ticker?: string
  token_name?: string
  token_image_url?: string
  launchpad_source?: string
  launchpad_status?: string
  bonding_progress_pct?: number
  primary_pool_addr?: string
  primary_dex?: string

  balance: string
  bought_token_raw?: string
  sold_token_raw?: string
  token_decimals?: number
  bought_ton?: string
  sold_ton?: string
  fees_ton?: string
  remaining_cost_basis_ton?: string
  realized_pnl_ton?: string
  avg_entry_ton?: string
  acquisition_basis_ton?: string
  bought_usd: string
  sold_usd: string
  /** Absent when the wallet's fees are not on the tape (positions L5,
      `fees_known: false`): unknown, never 0. */
  fees_usd?: string
  remaining_cost_basis_usd: string
  realized_pnl_usd: string
  /** Positions reader: false when realized_pnl_usd is only the part of the
      sales whose cost is known (its "0" is then not a zero); absent on an
      older read. */
  realized_complete?: boolean
  /** Positions reader: the basis status and the reasons behind a figure the
      read leaves out (basis_unknown / held_basis_unknown: no known cost, so
      unrealized and total PnL are not sent) */
  basis_status?: string
  pnl_reasons?: string[]
  /** Positions reader: the known units unrealized is computed from
      (valued_known_raw / 10^token_decimals × mark − remaining_cost_basis_usd);
      a live price moves unrealized on these alone. Absent when no unrealized
      is stated for a basis reason, with no position or unknown decimals. */
  valued_known_raw?: string
  /** The same for the TON figures: the units with a known TON cost (buys
      with no USD price included), TON-pure positions only. */
  valued_known_ton_raw?: string
  avg_entry_usd: string
  acquisition_basis_usd?: string
  hold_time_seconds?: number
  current_price_usd?: string
  current_mcap_usd?: string
  avg_entry_mcap_usd?: string
  current_price_ton?: string
  current_value_ton?: string
  unrealized_pnl_ton?: string
  total_pnl_ton?: string
  /** Return on what actually left the wallet: bought + every fee. */
  total_pnl_pct?: string
  current_value_usd?: string
  unrealized_pnl_usd?: string
  total_pnl_usd?: string
  price_available: boolean
  /** Positions reader (review F4): why a held row has no price. no_market —
      the row is "—" and left out of the totals; unread — not known now, the
      value total is "—" (price_coverage_complete false). */
  price_unavailable_reason?: 'no_market' | 'unread'
  /** Positions reader: the pool the row's price (mark) comes from. A live
      tick reprices the row only when it comes from this pool (review F3). */
  mark_pool?: string
  /** 'reserve': every pool of the token is under the floor; the row is worth
      what its drained pool pays now (operator rule 2026-11-03) — quote × q /
      (base + q), at most the pool's quote reserve. Its price is this
      holding's own, and no live tick reprices it (a tick from that pool would
      be a pre-drain print). */
  mark_source?: 'reserve'
  /** A 'reserve' row's mark: the pool's quote reserve in USD, its base
      reserve in raw units — a pushed balance revalues by the same rule. */
  mark_reserve_quote_usd?: string
  mark_reserve_base_raw?: string
  /** The projector could not account for THIS token's history, so none of its
      figures are stated. The row still carries its identity: a MISSING row is
      read as "the wallet holds none of it" and renders a confident zero for a
      position that exists. See backend portfolio.holding. */
  position_unavailable?: boolean
  unavailable_reason?: string
  first_acquired_at?: string
  last_activity_at?: string
  last_applied_wallet_event_id: number
  /** positions model: the balance in raw units (`balance` is the same in
      whole tokens) */
  balance_raw?: string
  /** journal mode (wiki/pr/JOURNAL-REALTIME-PUSH-20261102.md): the version of
      the wallet's journal row for this token, the order key of the
      `jettons.balance` frames on jettons:<owner> */
  quantity_version?: number | null
  /** journal mode, no figure: "no_journal_row" / "journal_error" */
  quantity_fallback?: string

  /** CLIENT-SIDE, never on the wire: set by mergeWalletPortfolios when one
      view sums several wallets — every wallet with a position in the token,
      in the view's order. Absent over one wallet. */
  client_wallets?: string[]
  /** CLIENT-SIDE, as client_wallets: those of them that may still hold it —
      a balance above zero, or one not known (an unknown never counts as
      sold). A Portfolio Sell reads it (positionSourceWallet). */
  client_held_in?: string[]
}

export type WalletPortfolio = {
  wallet_address: string
  holdings_count: number
  /** positions L5: false when the server could not check every holding (a
      bulk read failed, or a held token had no quantity source) — some may
      not be on the list at all */
  holdings_complete?: boolean
  /** how many of them had no quantity source (drawn "Position unavailable") */
  holdings_unresolved?: number
  total_txns?: number
  total_current_value_ton?: string
  /** Which reader answered: "wallet_token_positions" is the positions read
      (WT_POSITIONS_READ_PORTFOLIO), whose GRAM totals are its USD totals at
      ton_usd_rate; absent on the userland reader. */
  positions_source?: string
  /** CLIENT-SIDE (mergeWalletPortfolios): the USDT dollars inside the merged
      total_current_value_usd — each wallet's stated USDT value; the merged
      USDT row's own value is "" when one wallet's is unknown. */
  client_usdt_in_total_usd?: number
  /** The TON/USD the GRAM totals are stated at (the positions read; absent:
      not read, or a reader whose GRAM totals are its own). */
  ton_usd_rate?: string
  total_realized_pnl_ton?: string
  total_unrealized_pnl_ton?: string
  total_pnl_ton?: string
  total_current_value_usd: string
  total_realized_pnl_usd: string
  total_unrealized_pnl_usd: string
  total_pnl_usd: string
  holdings: WireHolding[]
  price_coverage_complete: boolean
  /** positions model: false when some row's PnL is not complete — realized,
      unrealized and total are then "" (unknown, "—"); absent on an older read */
  pnl_complete?: boolean
  pnl_includes_network_fees: boolean
  updated_through_wallet_event_id: number
  backfill_status: string
  backfill_note: string
}

/**
 * One call carries both the summary and the full holdings list, so Spot needs
 * no second round-trip. Returns null when the read gate is off or the API is
 * unreachable — the page then renders its empty state, never sample numbers.
 */
/** The waits before another try when the server says it is busy (positions
 *  L5: 503 portfolio_busy while four heavy portfolios are being built). */
export const BUSY_RETRY_MS = [1000, 2000, 4000] as const

/** A busy server is asked again, a little later, before the read gives up —
 *  a busy answer is not an empty portfolio. */
export async function withBusyRetry<T>(
  read: () => Promise<T>,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((done) => setTimeout(done, ms))
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await read()
    } catch (err) {
      const busy = err instanceof ApiError && err.status === 503 && err.code === 'portfolio_busy'
      const delay = BUSY_RETRY_MS[attempt]
      if (!busy || delay === undefined) throw err
      await wait(delay)
    }
  }
}

export async function fetchWalletPortfolio(address: string): Promise<WalletPortfolio | null> {
  try {
    return await withBusyRetry(() =>
      apiFetch<WalletPortfolio>(
        `/v1/wallets/${encodeURIComponent(address)}/portfolio${pnlCostQuery()}`
      )
    )
  } catch {
    return null
  }
}

// Composed Portfolio-page seed: ONE request carrying the exact bodies of
// /v1/wallets/:address/portfolio and .../live. The backend builds every
// section through the same code as the standalone endpoints. `live` is null
// when the balance preview is unavailable (balances gate off, cold-open
// failure) — exactly the standalone /live failure degrade.
export type WalletSeed = {
  portfolio: WalletPortfolio | null
  live: LiveBalancePreview | null
}

/**
 * Returns null when the read gate is off or the API is unreachable — the page
 * then renders its offline/empty states, same as the standalone calls
 * failing. A portfolio-section failure fails the whole seed, exactly as a
 * failed portfolio call fails the page today.
 */
export async function fetchWalletSeed(address: string): Promise<WalletSeed | null> {
  try {
    return await withBusyRetry(() =>
      apiFetch<WalletSeed>(`/v1/wallets/${encodeURIComponent(address)}/seed${pnlCostQuery()}`)
    )
  } catch {
    return null
  }
}

// Wallet activity event as served by /v1/wallets/:address/activity. Only the
// fields the trade-confirmation fallback binds on are typed; the wire carries
// more (amounts, fees, lt), which is intentionally not modeled here.
export type WalletActivityEvent = {
  id: number
  activity_key?: string
  activity_origin?: 'wallet_journal' | 'indexed_trade'
  event_type: string
  side?: string
  token_master?: string
  // Joined from token metadata by the API. Absent for a token we hold no
  // verified metadata for — the row then falls back to a short address rather
  // than inventing a symbol.
  token_ticker?: string
  token_name?: string
  token_amount?: string
  ts?: string
  tx_hash?: string
  // Valuation stamped at execution time by the wallet-event enricher. Strings,
  // never numbers: these are exact decimals and a JSON number would round them.
  ton_value_at_event?: string
  usd_value_at_event?: string
  fee_ton?: string
  fee_usd?: string
  lt?: string
  // The market cap at the trade and the token's logo, joined by the API
  // (the activity enrichment, backend portfolio/activity_feed.go). Absent
  // until that build is live, and for a trade it cannot value — the row
  // then falls back as it did before, never to a made-up figure.
  mcap_usd_at_event?: string
  token_image_url?: string
  // The token's launchpad and DEX, as the holdings rows carry them (the
  // avatar's badge and ring): asked of the activity enrichment for tokens the
  // wallets no longer hold. Absent until it ships.
  launchpad_source?: string
  launchpad_status?: string
  bonding_progress_pct?: number
  primary_dex?: string
  primary_pool_addr?: string
  /** CLIENT-SIDE, never on the wire: the wallet it was read from, set by
      useWalletHistory when one view reads several. Absent over one. */
  client_wallet?: string
}

export type WalletActivity = {
  items?: WalletActivityEvent[]
  // The API is explicit that it can only see what the userland indexer has
  // recorded. The History surface repeats that instead of implying "no trades".
  backfill_status?: string
  backfill_note?: string
}

export type CreatedTokenItem = {
  token_master: string
  /** CLIENT-SIDE, never on the wire: the wallet that launched it, set by
      useCreatedTokens when one view reads several wallets. */
  client_wallet?: string
  token_ticker?: string
  token_name?: string
  launchpad_source: string
  launchpad_status: string
  migrated: boolean
  bonding_progress_pct: number
  deploy_ts: string
  accrued_gram: string
  claimable_gram: string
  claimed_gram: string
  balance: string
  current_value_usd?: string
  total_pnl_usd?: string
}

export type CreatedTokensSummary = {
  /** the creator fees earned in all, where the API sends it */
  total_accrued_gram?: string
  launched_count: number
  migrated_count: number
  bonding_count: number
  total_claimable_gram: string
  total_claimed_gram: string
  portfolio_value_usd: string
  portfolio_total_pnl_usd: string
  /** The response's own price_coverage_complete, carried with its summary
   *  (hooks/useWalletHistory): false when some token had no price, so the
   *  value and the PnL count only the priced ones. */
  price_coverage_complete?: boolean
  /** The response's market_data_complete / market_data_limit (#1094),
   *  carried with its summary like the coverage flag: false when the API
   *  read market figures for only some tokens. */
  market_data_complete?: boolean
  market_data_limit?: number
}

export type WalletCreatedTokens = {
  summary?: CreatedTokensSummary
  items?: CreatedTokenItem[]
  price_coverage_complete?: boolean
  market_data_complete?: boolean
  market_data_limit?: number
  backfill_status?: string
  backfill_note?: string
}

/**
 * Tokens this wallet launched, with their creator-reward state. Same honesty
 * contract as the rest of the userland read surfaces: null means the gate is
 * off or the API is unreachable, an empty items list means "indexed nothing",
 * and neither is presented as "you launched nothing".
 */
export async function fetchWalletCreatedTokens(
  address: string
): Promise<WalletCreatedTokens | null> {
  try {
    return await apiFetch<WalletCreatedTokens>(
      `/v1/wallets/${encodeURIComponent(address)}/created-tokens`
    )
  } catch {
    return null
  }
}

/**
 * Recent wallet activity, newest first. Returns null when the read gate is
 * off or the API is unreachable — the caller degrades to its existing
 * confirmation path instead of surfacing an error.
 */
export async function fetchWalletActivity(
  address: string,
  limit = 10,
  source: 'wallet_journal' | 'indexed_trades' = 'indexed_trades'
): Promise<WalletActivity | null> {
  try {
    return await apiFetch<WalletActivity>(
      `/v1/wallets/${encodeURIComponent(address)}/activity?limit=${limit}&source=${source}`
    )
  } catch {
    return null
  }
}

// The booked realized curve and its coverage travel together. A covered empty
// read differs from a failed read or unchecked history; the latter cannot say
// that the wallet never sold anything.
export type RealizedPoint = { ts: string; realized_ton: string; realized_usd?: string }

export type RealizedPnLSnapshot = {
  points: RealizedPoint[]
  incomplete: boolean
  incomplete_reason?: string
  complete_through: string | null
}

export async function fetchRealizedPnLSnapshot(
  address: string,
  window: string
): Promise<RealizedPnLSnapshot | null> {
  try {
    const cost = pnlCostQuery()
    const sep = cost ? '&' : '?'
    const body = await apiFetch<Partial<RealizedPnLSnapshot>>(
      `/v1/wallets/${encodeURIComponent(address)}/realized-pnl${cost}${sep}window=${encodeURIComponent(window)}`
    )
    if (!body || !('points' in body) || (body.points != null && !Array.isArray(body.points)))
      return null
    const points = body.points ?? []
    if (
      points.some(
        (p) =>
          !p ||
          typeof p.ts !== 'string' ||
          !Number.isFinite(Date.parse(p.ts)) ||
          typeof p.realized_ton !== 'string'
      )
    )
      return null
    // Older servers also sent incomplete=false, without having checked tape
    // coverage. Only the new boundary field makes that completeness claim usable.
    const hasCoverage =
      typeof body.incomplete === 'boolean' &&
      (body.complete_through === null ||
        (typeof body.complete_through === 'string' &&
          Number.isFinite(Date.parse(body.complete_through))))
    return {
      points,
      incomplete: !hasCoverage || body.incomplete !== false,
      incomplete_reason: hasCoverage ? body.incomplete_reason : 'coverage_unchecked',
      complete_through: hasCoverage ? (body.complete_through ?? null) : null,
    }
  } catch {
    return null
  }
}

export async function fetchRealizedPnL(address: string, window: string): Promise<RealizedPoint[]> {
  return (await fetchRealizedPnLSnapshot(address, window))?.points ?? []
}

// A wallet's trades per local day (backend /v1/wallets/:address/daily-trades,
// the PnL calendar's day card; backend PR #1091). One entry per day that had
// a trade; a trade the API could not value is left out of its sum and
// counted in `unpriced`, per sum — and a side with no valued trade at all
// has no sum.
// Absent until that build is live: the call then fails, and the calendar
// shows what the realized curve knows alone.
export type DailyTrades = {
  day: string
  buys: number
  sells: number
  buy_usd?: string
  sell_usd?: string
  buy_ton?: string
  sell_ton?: string
  unpriced?: { buy_usd?: number; sell_usd?: number; buy_ton?: number; sell_ton?: number }
}

export async function fetchDailyTrades(
  address: string,
  from: string,
  to: string,
  tzOffsetMin: number
): Promise<DailyTrades[] | null> {
  // the browser's own zone when it names one: the API cuts days by it, so a
  // month across a daylight-saving change is cut as the calendar cuts it;
  // the fixed offset only when the browser names none
  let zone = ''
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
  } catch {
    zone = ''
  }
  const cut = zone ? `tz=${encodeURIComponent(zone)}` : `tz_offset_min=${tzOffsetMin}`
  try {
    const body = await apiFetch<{ days?: DailyTrades[] }>(
      `/v1/wallets/${encodeURIComponent(address)}/daily-trades?from=${from}&to=${to}&${cut}`
    )
    return body?.days ?? []
  } catch {
    return null
  }
}

// GET /v1/wallets/:address/daily-pnl — one wallet's PnL by UTC day. "" is
// unknown ("—"), "0" a measured zero; the page sums wallets
// (components/portfolio/dailyPnlMath.mergeDaily).
export type DailyPnLDay = {
  day: string
  ts: string
  realized_usd: string
  unrealized_usd: string
  unrealized_change_usd: string
  total_usd: string
  cum_realized_usd: string
  cum_total_usd: string
  // GRAM: the day's USD figure at TON/USD at the end of that UTC day ("":
  // the rate is unknown). Absent on a server older than 2026-11-04.
  realized_ton?: string
  unrealized_ton?: string
  unrealized_change_ton?: string
  total_ton?: string
  cum_realized_ton?: string
  cum_total_ton?: string
}

export type DailyPnLSnapshot = {
  days: DailyPnLDay[]
  incomplete: boolean
  incomplete_reasons: string[]
  /** every day carries its GRAM figures */
  hasTon: boolean
}

const DAILY_FIELDS = [
  'realized_usd',
  'unrealized_usd',
  'unrealized_change_usd',
  'total_usd',
  'cum_realized_usd',
  'cum_total_usd',
] as const

const DAILY_TON_FIELDS = [
  'realized_ton',
  'unrealized_ton',
  'unrealized_change_ton',
  'total_ton',
  'cum_realized_ton',
  'cum_total_ton',
] as const

/** null: the read failed, or the answer is not this shape. */
export async function fetchDailyPnLSnapshot(address: string): Promise<DailyPnLSnapshot | null> {
  try {
    const body = await apiFetch<Partial<DailyPnLSnapshot>>(
      `/v1/wallets/${encodeURIComponent(address)}/daily-pnl?window=all`
    )
    if (!body || !Array.isArray(body.days)) return null
    const ok = body.days.every(
      (d) =>
        d &&
        typeof d.day === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(d.day) &&
        typeof d.ts === 'string' &&
        DAILY_FIELDS.every((f) => typeof d[f] === 'string')
    )
    if (!ok) return null
    return {
      days: [...body.days].sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0)),
      incomplete: body.incomplete === true,
      incomplete_reasons: Array.isArray(body.incomplete_reasons) ? body.incomplete_reasons : [],
      hasTon: body.days.every((d) => DAILY_TON_FIELDS.every((f) => typeof d[f] === 'string')),
    }
  } catch {
    return null
  }
}
